<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-app>
			<div v-if="studioSettings">
				<Toolbar :items="toolbarItems" :panel-params="params" />
				<perfect-scrollbar :options="{suppressScrollY: true}">
					<v-container class="outer-container px-6 py-5 content" :class="{'dark-mode': darkMode}">
						<Channel
							v-for="(channel, key) in allChannels"
							:key="key"
							:all-channels="allChannels"
							:channel-data="channel"
							:midi-connected="midiConnected"
							:controller-type="controllerType"
							@update-assigned-channel="updateAssignedChannel"
						/>
					</v-container>
				</perfect-scrollbar>
			</div>
			<Loader :value="!studioSettings && !events" />
		</v-app>
	</v-defaults-provider>
</template>

<script>
/* Modules */
import Channel from "@/components/midi/Channel.vue";
import Loader from "@/components/Loader.vue";
import Toolbar from "@/components/Toolbar.vue";

/* Enums */
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import ConferenceRoles from "@/enums/ConferenceRoles";
import ControllerTypes from "@/enums/ControllerTypes";
import Panels from "@/enums/Panels";

/* Mixins */
import PanelPropertiesMixin from "@/mixins/PanelProperties";
import SequencerPlayerStudioStatus from "@/mixins/SequencerPlayerStudioStatus";

/* Libraries */
import {Controller} from "@/plugins/Controller";
import emitter from "../emitter";
import * as _ from "lodash";
import {VIcon, VSelect, VTooltip} from "vuetify/components";
import WorkerTimer from "@/plugins/WorkerTimer.js";

export default {
	name: "MidiUI",

	components: {
		Channel,
		Loader,
		Toolbar
	},

	mixins: [PanelPropertiesMixin, SequencerPlayerStudioStatus],

	props: ["params"],

	data() {
		return {
			// todo: comment to disable master channel handling
			masterChannel: [],
			trackChannels: [],
			hotkeyChannels: [],
			micChannels: [],

			// Cross-window MIDI command channels (MidiUI runs in a separate popout window)
			mainMidiBC: null,
			hotkeysMidiBC: null,

			// The actual popup Window object once popped out (see Main.vue#popoutPanel) -
			// this component instance keeps running in the main window's JS realm even
			// after its DOM moves into the popout, so resizing the real popup requires
			// this reference rather than the bare `window`.
			popoutWindowRef: null,

			// Controller
			midiConnected: false,
			controller: null,
			midiProfiles: [
				{
					title: "Default",
					value: ControllerTypes.Default
				},
				{
					title: "Rodecaster",
					value: ControllerTypes.Rodecaster
				}
			]
		};
	},

	computed: {
		toolbarItems() {
			return [
				{
					component: VSelect,
					outerClass: "ml-auto",
					props: {
						modelValue: this.controllerType,
						items: this.midiProfiles,
						bgColor: "white",
						density: "compact",
						variant: "outlined",
						singleLine: true,
						hideDetails: true
					},
					actions: {
						"update:modelValue": value => {
							this.controllerType = value;
						}
					}
				},
				{
					component: VTooltip,
					props: {
						location: "bottom",
						text: this.midiConnected ? "MIDI connected" : "MIDI disconnected"
					},
					slots: [
						{
							name: "activator",
							items: data => [
								{
									component: VIcon,
									props: {
										...data.props,
										class: "mx-4",
										icon: this.midiConnected ? "mdi-power-plug" : "mdi-power-plug-off",
										color: this.midiConnected ? "green" : "red"
									}
								}
							]
						}
					]
				}
			];
		},

		studioSettingsControllerAssignments() {
			if (this.midiConnected) {
				if (this.controllerType === ControllerTypes.Default) {
					return this.studioSettings?.preferences?.midi?.default;
				} else if (this.controllerType === ControllerTypes.Rodecaster) {
					return this.studioSettings?.preferences?.midi?.rodecaster;
				} else {
					return null;
				}
			} else {
				return null;
			}
		},

		controllerType: {
			get() {
				return this.studioSettings?.preferences?.midi?.midi_profile ?? ControllerTypes.Default;
			},

			set(value) {
				const studioSettings = this.studioSettings;
				studioSettings.preferences.midi.midi_profile = value;
				this.$store.commit("setStudioSettings", studioSettings);
			}
		},

		potsCount() {
			return this.studioSettings?.pots_count;
		},

		loopbackAudioDevice() {
			return this.studioSettings?.pc_audio_out_audition;
		},

		events() {
			return this.$store.getters.getSequencerItems(this.selectedStationID);
		},

		sequencerMode() {
			return this.$store.getters.getSequencerMode(this.selectedStationID);
		},

		availableModes() {
			return this.selectedStation?.AvailableModes;
		},

		activeStudioHotkeysOutputDevices() {
			return this.$store.getters.getActiveStudioHotkeysOutputDevices();
		},

		allChannels() {
			// todo: comment to enable master channel handling
			return this.trackChannels.concat(this.masterChannel, this.micChannels, this.hotkeyChannels);
			// return this.trackChannels.concat(this.micChannels, this.hotkeyChannels);
		},

		clientId() {
			return this.$store.getters.clientId();
		},

		darkMode() {
			return this.$store.getters.darkMode();
		}
	},

	watch: {
		controllerType(newControllerType, oldControllerType) {
			if (this.midiConnected && newControllerType !== oldControllerType) {
				this.controller = null;
				navigator.requestMIDIAccess({sysex: true}).then(access => {
					this.midiConnected = access.inputs.size > 0 && access.outputs.size > 0;
					this.onMIDISuccess(access);
				});
			}
		},

		allChannels() {
			this.resizePopoutWindow();
		},

		events() {
			// todo: comment to disable master channel handling
			this.handleMasterChannel();
			this.handleTrackChannels();
		},

		potsCount(value, previousValue) {
			if (this.events) {
				if (previousValue > value) {
					this.trackChannels.splice(value);
				} else {
					this.handleTrackChannels();
				}
			}
		},

		allPeers() {
			this.handleMicChannels();
		},

		activeStudioHotkeysOutputDevices() {
			this.handleHotkeyChannels();
		},

		userIsHost(isHost) {
			if (!isHost) {
				emitter.emit("docking:closePanel", this.params.api.panel);
			}
		}
	},

	created() {
		this.setupMIDI();
	},

	mounted() {
		this.mainMidiBC = new BroadcastChannel(`main-midi-channel-${this.clientId}`);
		this.hotkeysMidiBC = new BroadcastChannel(`hotkeys-midi-channel-${this.clientId}`);

		emitter.on("midiPopoutWindowOpened", this.handlePopoutWindowOpened);

		WorkerTimer.setTimeout(() => {
			// todo: comment to disable master channel handling
			this.handleMasterChannel();
			this.handleTrackChannels(true);
			this.handleMicChannels();
			this.handleHotkeyChannels();
		}, 1000);

		emitter.on("dump:pressed", () => {
			this.mainMidiBC.postMessage({type: "dumpButtonPressed"});
		});

		// on e.data[1] === 122
		emitter.on("change-mode:pressed", () => {
			const currentModeIndex = this.availableModes.findIndex(m => m === this.sequencerMode);
			this.setSequencerMode(this.availableModes[(currentModeIndex + 1) % this.availableModes.length]);
		});


		if (navigator.requestMIDIAccess) {
			navigator.requestMIDIAccess({sysex: true}).then(access => {
				this.midiConnected = access.inputs.size > 0 && access.outputs.size > 0;
				access.onstatechange = event => (this.midiConnected = event.port.state === "connected");
			}, this.onMIDIFailure);
		}

		window.addEventListener("beforeunload", () => {
			this.mainMidiBC.postMessage({type: "midiWindowClosed"});
			emitter.emit("resetAllChannels");

			this.mainMidiBC.close();
			this.hotkeysMidiBC.close();
		});
	},

	beforeUnmount() {
		this.mainMidiBC?.close();
		this.hotkeysMidiBC?.close();
		emitter.off("midiPopoutWindowOpened", this.handlePopoutWindowOpened);
	},

	methods: {
		findEvent(potNumber) {
			if (_.isEmpty(this.events)) {
				return null;
			}
			if (potNumber) {
				const event = this.events?.find(event =>
					event.EventType === EventTypes.SpotBlock
						? event.ChildItems?.some(child => child.PotNumber === potNumber)
						: event.PotNumber === potNumber
				);

				return event?.EventType === EventTypes.SpotBlock
					? event.ChildItems?.find(child => child.PotNumber === potNumber)
					: event;
			} else {
				const event = this.events?.find(event =>
					event.EventType === EventTypes.SpotBlock
						? event.ChildItems?.some(child =>
								[EventStatus.Current, EventStatus.Paused].includes(child.StatusCode)
							)
						: [EventStatus.Current, EventStatus.Paused].includes(event.StatusCode)
				);

				return event?.EventType === EventTypes.SpotBlock
					? event.ChildItems?.find(child =>
							[EventStatus.Current, EventStatus.Paused].includes(child.StatusCode)
						)
					: event;
			}
		},

		trackStatus(potNumber) {
			return this.findEvent(potNumber)?.StatusCode;
		},

		chainType(potNumber) {
			return this.findEvent(potNumber)?.Chain;
		},

		eventInPot(potNumber) {
			return this.findEvent(potNumber);
		},

		// todo: comment to disable master channel handling
		handleMasterChannel() {
			this.masterChannel[0] = {
				id: "master",
				mode: "master",
				name: "Master",
				assignment: this.studioSettingsControllerAssignments
					? this.studioSettingsControllerAssignments.find(a => a?.id === "master")?.assignment
					: this.potsCount < 4
						? `channel${this.potsCount + 1}`
						: null,
				channelNumber: 0,
				sequencerMode: this.sequencerMode,
				event: this.eventInPot(),
				chainType: this.chainType(),
				trackStatus: this.trackStatus(),
				mainMidiBC: this.mainMidiBC
			};
		},

		handleTrackChannels(setAssignment = false) {
			for (let pot = 1; pot <= this.potsCount; pot++) {
				const event = this.eventInPot(pot);

				this.trackChannels[pot - 1] = {
					id: event?.EventID,
					mode: "track_control",
					name: `Pot ${pot}`,
					assignment: setAssignment
						? this.studioSettingsControllerAssignments
							? this.studioSettingsControllerAssignments.find(a => a?.id === pot)?.assignment
							: `channel${pot}`
						: this.trackChannels[pot - 1]?.assignment,
					channelNumber: pot,
					sequencerMode: this.sequencerMode,
					event: event,
					chainType: this.chainType(pot),
					trackStatus: this.trackStatus(pot),
					mainMidiBC: this.mainMidiBC
				};
			}
		},

		handleMicChannels() {
			this.micChannels.splice(0, this.micChannels.length);
			this.allPeers.forEach((peer, index) => {
				if (peer.role !== ConferenceRoles.Guest) {
					this.micChannels[index] = {
						id: peer.studio ?? "sip-" + peer.studio,
						peerID: peer.peerID,
						mode: "mic_control",
						name: `Mic ${peer.studio}`,
						assignment: this.studioSettingsControllerAssignments
							? this.studioSettingsControllerAssignments.find(
									a => a?.id === (peer.studio ?? `sip-${peer.studio}`)
								)?.assignment
							: this.micChannels[index]?.assignment,
						channelNumber: index,
						role: peer.role,
						loopbackDevice: this.loopbackAudioDevice,
						mainMidiBC: this.mainMidiBC
					};
				}
			});
		},

		handleHotkeyChannels() {
			if (_.isEmpty(this.activeStudioHotkeysOutputDevices)) {
				this.hotkeyChannels.splice(0, this.hotkeyChannels.length);
				this.hotkeyChannels.push({
					id: "default",
					mode: "hotkey_control",
					name: "Hotkey bank",
					assignment: this.studioSettingsControllerAssignments
						? this.studioSettingsControllerAssignments.find(a => a.id === "default")?.assignment
						: null,
					channelNumber: 0,
					bankAudioDevice: "default",
					auditionAudioDevice: this.studioSettings?.pc_audio_out_audition,
					hotkeysMidiBC: this.hotkeysMidiBC
				});
			} else {
				this.activeStudioHotkeysOutputDevices.forEach((device, index) => {
					this.hotkeyChannels[index] = {
						id: device.device,
						name: `Hotkey bank ${this.soundCardName(index)}`,
						mode: "hotkey_control",
						assignment: this.studioSettingsControllerAssignments
							? this.studioSettingsControllerAssignments.find(a => a?.id === device.device)?.assignment
							: this.hotkeyChannels[index]?.assignment,
						channelNumber: index,
						bankAudioDevice: this.activeStudioHotkeysOutputDevices[index],
						auditionAudioDevice: this.studioSettings?.pc_audio_out_audition,
						hotkeysMidiBC: this.hotkeysMidiBC
					};
				});
			}
		},

		handleChannelAssignments() {
			if (this.studioSettingsControllerAssignments) {
				this.allChannels = this.allChannels.map((channel, index) => {
					channel.assignment = this.studioSettingsControllerAssignments[index];
				});
			}
		},

		soundCardName(index) {
			return (index + 10).toString(36).toUpperCase();
		},

		setSequencerMode(mode) {
			emitter.emit("midi:setSequencerMode", mode);
		},

		updateAssignedChannel(ev) {
			if (ev.channelName) {
				// Index of previous channel that has channelName assigned to it
				let previousIndex = this.allChannels.findIndex(channel => {
					return channel.assignment === ev.channelName;
				});
				// If channelName was assigned to another channel
				if (previousIndex > -1) {
					if (this.allChannels[ev.channelIndex].assignment) {
						emitter.emit("onSetChannelName", {
							channel: previousIndex,
							channelName: ""
						});
						this.allChannels[previousIndex].assignment = this.allChannels[ev.channelIndex].assignment;
						emitter.emit("onSetChannelName", {
							channel: previousIndex,
							channelName: this.allChannels[previousIndex].name
						});
						emitter.emit("updateControllerColor", {
							newlyAssignedChannel: this.allChannels[previousIndex].assignment
						});
					} else {
						this.allChannels[previousIndex].assignment = null;
					}
				}
			}

			this.allChannels[ev.channelIndex].assignment = ev.channelName;
			emitter.emit("onSetChannelName", {
				channel: ev.channelIndex,
				channelName: this.allChannels[ev.channelIndex].name
			});
			emitter.emit("updateControllerColor", {
				newlyAssignedChannel: ev.channelName
			});
			emitter.emit("updateControllerFader", {
				newlyAssignedChannel: ev.channelName
			});

			this.controller.channels = this.allChannels;
		},

		editStudioSettings(payload) {
			this.mainMidiBC.postMessage({
				type: "editStudioSettings",
				payload: JSON.stringify(payload)
			});
		},

		// ============================ Controller =================================== //
		setupMIDI() {
			if (navigator.requestMIDIAccess) {
				navigator.requestMIDIAccess({sysex: true}).then(access => {
					this.midiConnected = access.inputs.size > 0 && access.outputs.size > 0;
					this.onMIDISuccess(access);
					access.onstatechange = event => {
						if (event.port.type !== "input") {
							return;
						}

						this.midiConnected = event.port.state === "connected";

						if (this.midiConnected) {
							if (event.port.manufacturer.toLowerCase().includes("rode")) {
								this.controllerType = ControllerTypes.Rodecaster;
							}
							this.controller = null;
							this.onMIDISuccess(access);
							WorkerTimer.setTimeout(() => {
								emitter.emit("reinitializeMIDI");
							}, 1000);
						} else if (this.controller) {
							this.controller.midiConnected = false;
							this.controller = null;
						}
					};
				}, this.onMIDIFailure);
			}
		},

		onMIDISuccess(midiAccess) {
			if (!this.midiConnected || this.controller) {
				return;
			}

			let inputPort = null;
			let outputPort = null;
			for (const entry of midiAccess.inputs) {
				inputPort = entry[1];
			}
			for (const entry of midiAccess.outputs) {
				outputPort = entry[1];
			}

			if (this.midiConnected) {
				WorkerTimer.setTimeout(() => {
					this.controller = new Controller(
						{inputPort, outputPort},
						this.allChannels,
						this.midiConnected,
						this.controllerType
					);
				}, 1000);
			}
		},

		onMIDIFailure(msg) {
			this.midiConnected = false;
			console.error(`Failed to get MIDI access - ${msg}`);
		},
		// ============================ Controller End ============================= //

		handlePopoutWindowOpened(popoutWindow) {
			this.popoutWindowRef = popoutWindow;
			this.resizePopoutWindow();
		},

		resizePopoutWindow() {
			if (!this.popoutWindowRef || this.popoutWindowRef.closed) {
				return;
			}
			const minWidth = Panels.midi.floating?.width ?? 0;
			const minHeight = Panels.midi.floating?.height ?? 0;
			this.popoutWindowRef.resizeTo(Math.max(160 * this.allChannels.length, minWidth), Math.max(900, minHeight));
		}
	}
};
</script>

<style scoped lang="scss">
.outer-container {
	background-color: $ihm-white;
	display: flex;
	max-width: 100%;
}

.dark-mode {
	background-color: $grey-500;
}
</style>
